import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, ConfirmDialog, Input, Modal, PageHeader, Spinner } from '../components/ui';
import { workspaceService, type WorkspaceStatus, type WorkspaceSummary } from '../services/workspaceService';

const variants: Record<WorkspaceStatus, 'warning' | 'success' | 'danger' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  suspended: 'default',
};

export default function PlatformWorkspacesPage() {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [pendingReview, setPendingReview] = useState<{
    workspace: WorkspaceSummary;
    status: 'approved' | 'rejected' | 'suspended';
  } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setWorkspaces(await workspaceService.list());
    } catch (error) {
      toast.error('Could not load workspaces', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreating(true);
    try {
      await workspaceService.createApproved(name.trim(), adminEmail.trim().toLowerCase());
      setName('');
      setAdminEmail('');
      setCreateOpen(false);
      toast.success('Workspace created and approved');
      await load();
    } catch (error) {
      toast.error('Could not create workspace', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setCreating(false);
    }
  };

  const review = async () => {
    if (!pendingReview) return;
    setReviewing(true);
    try {
      await workspaceService.review(pendingReview.workspace.id, pendingReview.status);
      toast.success(`Workspace ${pendingReview.status}`);
      setPendingReview(null);
      await load();
    } catch (error) {
      toast.error('Could not update workspace', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setReviewing(false);
    }
  };

  const actionLabel = pendingReview?.status === 'approved'
    ? 'Approve'
    : pendingReview?.status === 'rejected'
      ? 'Reject'
      : 'Suspend';

  return <>
    <div className="p-8 max-w-5xl">
      <PageHeader
        title="Platform workspaces"
        description="Create approved workspaces and review organizer applications."
        action={<Button icon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Create workspace</Button>}
      />
      {loading ? <Spinner /> : <div className="rounded-2xl border border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-gray-400"><tr><th className="text-left p-3">Workspace</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr></thead>
          <tbody>{workspaces.map((workspace) => <tr key={workspace.id} className="border-t border-gray-800">
            <td className="p-3 font-medium text-white">{workspace.name}</td>
            <td className="p-3"><Badge variant={variants[workspace.status]}>{workspace.status}</Badge></td>
            <td className="p-3 text-right space-x-2">
              {workspace.status !== 'approved' && <Button size="sm" onClick={() => setPendingReview({ workspace, status: 'approved' })}>Approve</Button>}
              {workspace.status !== 'rejected' && <Button size="sm" variant="ghost" onClick={() => setPendingReview({ workspace, status: 'rejected' })}>Reject</Button>}
              {workspace.status !== 'suspended' && <Button size="sm" variant="danger" onClick={() => setPendingReview({ workspace, status: 'suspended' })}>Suspend</Button>}
            </td>
          </tr>)}</tbody>
        </table>
      </div>}
    </div>
    <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create workspace" description="The workspace is approved immediately and assigned to an existing verified account.">
      <form onSubmit={create} className="space-y-4">
        <Input label="Workspace name" value={name} onChange={(event) => setName(event.target.value)} required minLength={2} maxLength={160} />
        <Input label="Initial admin email" type="email" value={adminEmail} onChange={(event) => setAdminEmail(event.target.value)} required hint="The account must already exist and be verified." />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button type="submit" loading={creating}>Create approved workspace</Button>
        </div>
      </form>
    </Modal>
    <ConfirmDialog
      open={pendingReview !== null}
      onClose={() => setPendingReview(null)}
      onConfirm={() => void review()}
      loading={reviewing}
      title={`${actionLabel} workspace?`}
      description={`${actionLabel} ${pendingReview?.workspace.name ?? 'this workspace'}? This changes organizer access immediately.`}
      confirmLabel={`${actionLabel} workspace`}
      intent={pendingReview?.status === 'approved' ? 'primary' : 'danger'}
    />
  </>;
}
